import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ReadingLevelTest.css';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  CircularProgress,
  Alert,
  Box,
  LinearProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import { API_BASE_URL } from '../utils/api';

interface Question {
  id: number;
  type: string;
  question: string;
  passage?: string;
  options: string[];
  correct_answer: number;
}

const ReadingLevelTest: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_BASE_URL}/api/student/diagnostic/questions`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setQuestions(response.data.questions);
      setAnswers(new Array(response.data.questions.length).fill(-1));
    } catch (err: any) {
      setError(err.response?.data?.error || t('readingLevelTest.errors.loadQuestions'));
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (value: number) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = value;
    setAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    // Check if all questions are answered
    if (answers.some(a => a === -1)) {
      setError(t('readingLevelTest.errors.answerAll') || t('readingLevelTest.answerAllQuestions'));
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API_BASE_URL}/api/student/diagnostic/submit`,
        { answers },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Navigate to dashboard with success message
      navigate('/student/dashboard', {
        state: {
          diagnosticComplete: true,
          readingLevel: response.data.reading_level,
          score: response.data.score
        }
      });
    } catch (err: any) {
      setError(err.response?.data?.error || t('readingLevelTest.errors.submit'));
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="test-loading">
        <CircularProgress />
        <Typography variant="h6" sx={{ marginTop: 2 }}>
          {t('readingLevelTest.loading')}
        </Typography>
      </div>
    );
  }

  if (error && !questions.length) {
    return (
      <div className="test-container">
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/student/assessment')} sx={{ marginTop: 2 }}>
          {t('common.back')}
        </Button>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const allAnswered = !answers.some(a => a === -1);

  return (
    <div className="test-container">
      <Card className="test-card">
        <CardContent>
          {/* Progress Bar */}
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('readingLevelTest.question')} {currentQuestion + 1} {t('common.of')} {questions.length}
            </Typography>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
          </Box>

          {/* Question */}
          <div className="question-section">
            {question.passage && (
              <div className="passage-box">
                <Typography variant="h6" gutterBottom>
                  {t('readingLevelTest.readPassage')}
                </Typography>
                <Typography variant="body1" className="passage-text">
                  {question.passage}
                </Typography>
              </div>
            )}

            <Typography variant="h5" component="h2" gutterBottom className="question-text">
              {question.question}
            </Typography>

            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={answers[currentQuestion] >= 0 ? answers[currentQuestion].toString() : ''}
                onChange={(e) => handleAnswerChange(parseInt(e.target.value))}
              >
                {question.options.map((option, index) => (
                  <FormControlLabel
                    key={index}
                    value={index.toString()}
                    control={<Radio />}
                    label={option}
                    className="option-label"
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </div>

          {/* Navigation */}
          <div className="test-navigation">
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
            >
              {t('common.previous')}
            </Button>

            {currentQuestion === questions.length - 1 ? (
              <Button
                variant="contained"
                endIcon={<CheckCircleIcon />}
                onClick={handleSubmit}
                disabled={!allAnswered || submitting}
                color="success"
              >
                {submitting ? (
                  <>
                    <CircularProgress size={20} sx={{ marginRight: 1 }} />
                    {t('readingLevelTest.submitting')}
                  </>
                ) : (
                  t('readingLevelTest.submit')
                )}
              </Button>
            ) : (
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={handleNext}
                disabled={answers[currentQuestion] === -1}
              >
                {t('common.next')}
              </Button>
            )}
          </div>

          {error && (
            <Alert severity="error" sx={{ marginTop: 2 }}>
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReadingLevelTest;

